import React, { useEffect } from "react";
import { Col, Container, Row, Card } from "react-bootstrap";
import { FaSave } from "react-icons/fa";
import { FaUser } from "react-icons/fa6";
import { Link, useNavigate } from "react-router-dom";
import ProfileSettingsTabs from "./Accounts/ProfileSettingsTabs";
import { useAuth } from "provider/AuthContext";
import { useProfileContext } from "provider/ProfileProvider";
import apiCall from "components/Api/api";
import { handleError } from "components/Api/handleError";
import { useToast } from "provider/ToastProvider";
import { useScreenSize } from "provider/ScreenSizeProvider";
import Cookies from "js-cookie";

const Account = () => {
  const { userProfile, logout, login } = useAuth();
  const { isMobileView } = useScreenSize();
  const { genInfoForm, activeTab, genInfoLoading, genInfoErrors,
    profilePhotoFile, profilePhotoLoading,
    coverPhotoFile, coverPhotoLoading,
    profileDispatch } = useProfileContext();
  const { showSuccessToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        profileDispatch({ type: "SET_ACCOUNT_LOADING", payload: true });
        const response = await apiCall({
          url: `user-profile?user_handle=${userProfile?.user_handle}`,
        });

        if (response.status !== 200) {
          throw new Error(`Unexpected status code: ${response.status}`);
        }

        if (response?.status === 200) {
          if (response?.data?.errors) {
            const errors = response?.data.errors;
            profileDispatch({
              type: "SET_ACCOUNT_ERROR",
              payload: errors,
            });
            console.error(errors)
          } else {
            const profileData = response?.data;
            profileDispatch({ type: 'SET_USER_ACCOUNT_SETTING_DATA', payload: profileData });

          }
        }
      } catch (error) {
        console.error("An error occurred while fetching user profile:", error);
        handleError(error, logout, navigate);
      } finally {
        profileDispatch({ type: "SET_ACCOUNT_LOADING", payload: false });
      }
    };

    fetchUserProfile();
  }, [userProfile?.user_handle]);

  //for General Info Form
  const isValidUrl = (url) => {
    return /^(https?:\/\/)?([a-z0-9-]+\.)?[a-z0-9-]+\.[a-z]{2,}(\/\S*)?$/i.test(url);
  };

  const validateForm = () => {
    const newErrors = {};

    // Required: name
    if (!genInfoForm.name.trim()) {
      newErrors.name = "Name is required";
    }

    // Required: user_handle
    if (!genInfoForm.user_handle.trim()) {
      newErrors.user_handle = "Username is required";
    } else if (!/^[a-z0-9_-]+$/i.test(genInfoForm.user_handle)) {
      newErrors.user_handle = "Username can only contain letters, numbers, and underscores";
    }

    // Validate mobile_no only if it's filled
    if (genInfoForm.mobile_no.trim() && !/^[6-9]\d{9}$/.test(genInfoForm.mobile_no)) {
      newErrors.mobile_no = "Enter a valid 10-digit mobile number";
    }

    // Validate date_of_birth if filled
    if (genInfoForm.date_of_birth && !/^\d{4}-\d{2}-\d{2}$/.test(genInfoForm.date_of_birth)) {
      newErrors.date_of_birth = "Date of Birth must be in YYYY-MM-DD format";
    }

    // Validate gender if selected (expecting a number or string value)
    if (genInfoForm.gender && ![1, 2, 3].includes(Number(genInfoForm.gender))) {
      newErrors.gender = "Invalid gender selected";
    }

    // Validate user_bio if filled
    if (genInfoForm.user_bio.trim() && genInfoForm.user_bio.length < 10) {
      newErrors.user_bio = "Profile description must be at least 10 characters";
    }

    // Validate about if filled
    if (genInfoForm.about.trim() && genInfoForm.about.length < 10) {
      newErrors.about = "About section must be at least 10 characters";
    }

    // Validate optional social URLs
    const urlFields = {
      facebook_url: "https://facebook.com/yourprofile",
      x_url: "https://x.com/yourhandle",
      insta_url: "https://instagram.com/yourprofile",
      youtube_url: "https://youtube.com/channel/yourchannel",
      website_url: "https://yourwebsite.com"
    };

    Object.entries(urlFields).forEach(([field, example]) => {
      const value = genInfoForm[field]?.trim();
      if (value && !isValidUrl(value)) {
        newErrors[field] = `Enter a valid URL (e.g., ${example})`;
      }
    });

    profileDispatch({
      type: "SET_GEN_INFO_ERRORS",
      payload: newErrors,
    });

    return Object.keys(newErrors).length === 0;
  };


  const fetchUserProfile = async (user_handle) => {
    try {
      profileDispatch({ type: "SET_ACCOUNT_LOADING", payload: true });
      const response = await apiCall({
        url: `user-profile?user_handle=${user_handle}`,
      });

      if (response.status !== 200) {
        throw new Error(`Unexpected status code: ${response.status}`);
      }

      if (response?.status === 200) {
        if (response?.data?.errors) {
          const errors = response?.data.errors;
          profileDispatch({
            type: "SET_ACCOUNT_ERROR",
            payload: errors,
          });
          console.error(errors)
        } else {
          const profileData = response?.data;
          profileDispatch({ type: 'SET_USER_ACCOUNT_SETTING_DATA', payload: profileData });
          const authToken = Cookies.get("auth_token");
          login(profileData, authToken);
          if (profileData?.user_handle === userProfile?.user_handle) {
            profileDispatch({ type: 'SET_PROFILE_DATA', payload: profileData });
          }
          if (profileData?.user_handle === user_handle) {
            profileDispatch({ type: 'SET_PROFILE_DATA', payload: profileData });
          }
        }
      }
    } catch (error) {
      console.error("An error occurred while fetching user profile:", error);
      handleError(error, logout, navigate);
    } finally {
      profileDispatch({ type: "SET_ACCOUNT_LOADING", payload: false });
    }
  };


  const handleGenInfoSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      console.warn("Form has validation errors.");
      return;
    }

    try {
      profileDispatch({
        type: "SET_GEN_INFO_LOADING",
        payload: true
      });

      const formData = new FormData();
      Object.entries(genInfoForm).forEach(([key, value]) => {
        if (
          value !== null &&
          value !== undefined &&
          (
            (typeof value === "number" && (key !== "gender" || [1, 2, 3].includes(value))) ||
            (typeof value === "string" && value.trim() !== "")
          )
        ) {
          formData.append(key, value);
        }
      });


      const response = await apiCall({
        url: "account",
        method: "POST",
        data: formData,
      });
      if (response.status !== 200) {
        throw new Error(`Unexpected status code: ${response.status}`);
      }

      const apiData = response.data;
      if (apiData?.errors && Object.keys(apiData.errors).length > 0) {
        const errors = apiData.errors;
        // Dynamically set errors based on the response
        Object.keys(errors).forEach((key) => {
          const errorMessages = errors[key];
          const firstErrorMessage = errorMessages[0];
          // Set the error state for the corresponding field
          profileDispatch({
            type: "SET_GEN_INFO_ERRORS",
            payload: {
              ...genInfoErrors,
              [key]: firstErrorMessage,
            },
          });

        });
        return;
      }
      if (apiData?.status === 1) {
        showSuccessToast(apiData?.message);

        fetchUserProfile(genInfoForm.user_handle);


      }
    } catch (error) {
      console.error("Error submitting form:", error.message);
      // Optionally show error UI feedback here
      handleError(error, logout, navigate);

    } finally {

      profileDispatch({
        type: "SET_GEN_INFO_LOADING",
        payload: false
      });
    }
  };

  const updateProfilePhoto = async (e) => {
    e.preventDefault();

    // Validate before submit
    if (!profilePhotoFile) {
      profileDispatch({
        type: "SET_PROFILE_PHOTO_ERROR",
        payload: "Please select a profile photo.",
      });
      return;
    }

    // Validate file type and size again before submit for safety
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const maxSize = 250 * 1024; // 250KB

    if (!allowedTypes.includes(profilePhotoFile.type)) {
      profileDispatch({
        type: "SET_PROFILE_PHOTO_ERROR",
        payload: "Only JPG, JPEG, PNG files are allowed.",
      });
      return;
    }

    if (profilePhotoFile.size > maxSize) {
      profileDispatch({
        type: "SET_PROFILE_PHOTO_ERROR",
        payload: "File size must be under 250KB.",
      });
      return;
    }

    try {
      profileDispatch({ type: "SET_PROFILE_PHOTO_LOADING", payload: true });
      profileDispatch({ type: "SET_PROFILE_PHOTO_ERROR", payload: "" });

      const formData = new FormData();
      formData.append("profile_image", profilePhotoFile);

      const response = await apiCall({
        url: "account/profile-photo",
        method: "POST",
        data: formData,
      });

      if (response.status !== 200) {
        throw new Error(`Unexpected status code: ${response.status}`);
      }

      const apiData = response.data;

      if (apiData?.errors && Object.keys(apiData.errors).length > 0) {
        const firstErrorKey = Object.keys(apiData.errors)[0];
        const firstErrorMessage = apiData.errors[firstErrorKey][0];

        return profileDispatch({
          type: "SET_PROFILE_PHOTO_ERROR",
          payload: firstErrorMessage,
        });
      }

      if (apiData?.status === 1) {
        showSuccessToast(apiData?.message);

        fetchUserProfile(genInfoForm.user_handle);

        // Optionally clear selected file after successful upload
        profileDispatch({ type: "SET_PROFILE_PHOTO_FILE", payload: null });
        profileDispatch({ type: "SET_ACTIVE_TAB", payload: "general" });
      }
    } catch (error) {
      console.error("Error submitting profile photo:", error.message);
      handleError(error, logout, navigate);
    } finally {
      profileDispatch({ type: "SET_PROFILE_PHOTO_LOADING", payload: false });
    }
  };

  const updateCoverPhoto = async (e) => {
    e.preventDefault();

    // Validate before submit
    if (!coverPhotoFile) {
      profileDispatch({
        type: "SET_COVER_PHOTO_ERROR",
        payload: "Please select a cover photo.",
      });
      return;
    }

    // Validate file type and size again before submit for safety
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const maxSize = 250 * 1024; // 250KB

    if (!allowedTypes.includes(coverPhotoFile.type)) {
      profileDispatch({
        type: "SET_COVER_PHOTO_ERROR",
        payload: "Only JPG, JPEG, PNG files are allowed.",
      });
      return;
    }

    if (coverPhotoFile.size > maxSize) {
      profileDispatch({
        type: "SET_COVER_PHOTO_ERROR",
        payload: "File size must be under 250KB.",
      });
      return;
    }

    try {
      profileDispatch({ type: "SET_COVER_PHOTO_LOADING", payload: true });
      profileDispatch({ type: "SET_COVER_PHOTO_ERROR", payload: "" });

      const formData = new FormData();
      formData.append("cover_image", coverPhotoFile);

      const response = await apiCall({
        url: "account/cover-photo",
        method: "POST",
        data: formData,
      });

      if (response.status !== 200) {
        throw new Error(`Unexpected status code: ${response.status}`);
      }

      const apiData = response.data;

      if (apiData?.errors && Object.keys(apiData.errors).length > 0) {
        const firstErrorKey = Object.keys(apiData.errors)[0];
        const firstErrorMessage = apiData.errors[firstErrorKey][0];

        return profileDispatch({
          type: "SET_COVER_PHOTO_ERROR",
          payload: firstErrorMessage,
        });
      }

      if (apiData?.status === 1) {
        showSuccessToast(apiData?.message);

        fetchUserProfile(genInfoForm.user_handle);

        profileDispatch({ type: "SET_COVER_PHOTO_FILE", payload: null });
        profileDispatch({ type: "SET_ACTIVE_TAB", payload: "general" });
      }
    } catch (error) {
      console.error("Error submitting cover photo:", error.message);
      handleError(error, logout, navigate);
    } finally {
      profileDispatch({ type: "SET_COVER_PHOTO_LOADING", payload: false });
    }
  };

  useEffect(() => {
    profileDispatch({ type: "SET_ACTIVE_TAB", payload: "general" });
  }, [])

  const handleSubmit = (e) => {
    e.preventDefault();

    if (activeTab === "general") {
      handleGenInfoSubmit(e);
    } else if (activeTab === "profile-photo") {
      updateProfilePhoto(e);
    } else if (activeTab === "cover-photo") {
      updateCoverPhoto(e);
    }
  };

  //Handle disable
  const isSubmitting =
    (activeTab === "general" && genInfoLoading) ||
    (activeTab === "profile-photo" && profilePhotoLoading) ||
    (activeTab === "cover-photo" && coverPhotoLoading);

  return (
    <>
      <Container fluid="lg" className="mt-5 pt-5">
        <Row className="margin_bottomfooter">
          <Col
            xs={12}
            className="d-flex align-items-center justify-content-between flex-wrap mb-4"
          >
            <h6 className="fs-3 fw-bold ">Account Settings</h6>
            <div className="unsaved_changes text-danger"></div>
            <div className="justify-content-between">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="btn btn-info bg-blues py-2 rounded-5"
              >
                <FaSave className="me-1" />
                Save Changes
              </button>
              <Link
                to={`/profile/user/${userProfile?.user_handle}`}
                className="btn btn-info bg-blues mx-2 py-2 rounded-5"
              >
                View Profile
              </Link>
            </div>
          </Col>

          {isMobileView && <Col xs={12} className="d-lg-none mb-4">
            <div className="itenary_tabs">
              <div className="card account-settingside sidebar_account safartabs">
                <div className="nav nav-tabs nav-pills card-body slider_accountsidebar">
                  <Link to="/account" className="nav-link link-nav active">
                    <FaUser className="me-1" style={{ width: '2rem' }} />
                    Profile Settings
                  </Link>
                  {/* <Link to="/account/privacy" className="nav-link link-nav">
                    <FaLock className="me-1" style={{ width: '2rem' }} />
                    Privacy
                  </Link>
                  <Link
                    to="/account/blocked-member"
                    className="nav-link link-nav"
                  >
                    <FaUserLock className="me-1" style={{ width: '2rem' }} />
                    Blocked Members
                  </Link> */}
                </div>
              </div>
            </div>
          </Col>
          }
          {/* Desktop Navbar */}
          {!isMobileView && <Col xxl={3} lg={4} className="d-none d-lg-block mb-4">
            <Card className="sidebar_account">
              <Card.Body className="nav nav-tabs flex-column nav-pills slider_accountsidebar">
                <Link to="/account" className="nav-link mb-2 active">
                  <FaUser className="me-2" />
                  Profile Settings
                </Link>
                {/* <Link to="/account/privacy" className="nav-link mb-2">
                  <FaLock className="me-2" />
                  Privacy
                </Link>
                <Link to="/account/blocked-member" className="nav-link mb-2">
                  <FaUserLock className="me-2" />
                  Blocked Members
                </Link> */}
              </Card.Body>
            </Card>
          </Col>
          }
          <Col xxl={9} lg={8} className="itenary_tabs account-settings-wrapper">
            <ProfileSettingsTabs genInfoSubmit={handleGenInfoSubmit} profilePhotoSubmit={updateProfilePhoto} coverPhotoSubmit={updateCoverPhoto} />
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Account;
